"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Eye, ShieldAlert, Cable, SlidersHorizontal, ArrowRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
export default function Placement() {
  const shouldReduceMotion = useReducedMotion();
  const motionInitial = shouldReduceMotion ? {
    opacity: 1,
    y: 0
  } : {
    opacity: 0,
    y: 12
  };
  const motionAnimate = {
    opacity: 1,
    y: 0
  };
  const motionTransition = {
    duration: 0.3,
    ease: [0.16, 1, 0.3, 1]
  };
  return <section id="operatorcontrollerplacement" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 max-w-4xl">
        <motion.div initial={motionInitial} whileInView={motionAnimate} viewport={{
        once: true
      }} transition={motionTransition} className="flex flex-col items-center">
          {/* Classification Eyebrow */}
          <div className="flex items-center gap-2 mb-4">
            <span className="inline-block size-1.5 bg-primary" />
            <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
              OPERATIONAL SETUP // CONSOLE POSITIONING
            </span>
            <span className="inline-block size-1.5 bg-primary" />
          </div>

          {/* Section Heading */}
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold uppercase tracking-tight text-center text-foreground max-w-2xl mb-8">
            Position the Operator Controller Carefully
          </h2>

          {/* Technical Spec Plate Card */}
          <Card className="w-full bg-card text-card-foreground border border-border shadow-xs relative rounded-none p-0 overflow-visible">
            {/* Corner Registration Ticks */}
            <div className="absolute -top-1.5 -left-1.5 size-3 border-t-2 border-l-2 border-primary pointer-events-none" />
            <div className="absolute -top-1.5 -right-1.5 size-3 border-t-2 border-r-2 border-primary pointer-events-none" />
            <div className="absolute -bottom-1.5 -left-1.5 size-3 border-b-2 border-l-2 border-primary pointer-events-none" />
            <div className="absolute -bottom-1.5 -right-1.5 size-3 border-b-2 border-r-2 border-primary pointer-events-none" />

            {/* Spec Plate Header Strip */}
            <div className="bg-muted/60 border-b border-border px-6 py-3 flex flex-wrap items-center justify-between gap-3 font-mono text-xs text-muted-foreground">
              <div className="flex items-center gap-2">
                <SlidersHorizontal className="size-3.5 text-primary" />
                <span>SPEC DOC // SEC-CTRL-04</span>
              </div>
              <Badge variant="outline" className="rounded-none font-mono text-[10px] tracking-wider uppercase border-border bg-background">
                Mandatory Protocol
              </Badge>
            </div>

            <CardContent className="p-6 md:p-10 space-y-8">
              {/* Guidance Item 1: Visibility and Sightlines */}
              <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start">
                <div className="md:col-span-3 flex items-center md:flex-col md:items-start gap-3">
                  <div className="flex size-10 items-center justify-center bg-primary/10 border border-primary/30 text-primary shrink-0">
                    <Eye className="size-5" />
                  </div>
                  <div>
                    <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground block">
                      Clear Sightline
                    </span>
                    <span className="font-serif font-semibold text-foreground text-sm uppercase">
                      Operating Field
                    </span>
                  </div>
                </div>

                <div className="md:col-span-9">
                  <p className="font-sans text-base md:text-lg leading-relaxed text-foreground">
                    The operator needs a clear view of the rider and the complete operating area. Therefore, controller placement should support visibility and access to the machine&apos;s controls without creating unnecessary obstacles around the setup.
                  </p>
                </div>
              </div>

              <Separator className="bg-border" />

              {/* Guidance Item 2: Machine Instructions & Cable Safety */}
              <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start">
                <div className="md:col-span-3 flex items-center md:flex-col md:items-start gap-3">
                  <div className="flex size-10 items-center justify-center bg-primary/10 border border-primary/30 text-primary shrink-0">
                    <Cable className="size-5" />
                  </div>
                  <div>
                    <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground block">
                      Routing Safety
                    </span>
                    <span className="font-serif font-semibold text-foreground text-sm uppercase">
                      Cable Pathway
                    </span>
                  </div>
                </div>

                <div className="md:col-span-9">
                  <p className="font-sans text-base md:text-lg leading-relaxed text-foreground">
                    Use the instructions for the exact controller and machine when deciding where it should sit. In addition, route cables so they do not create avoidable trip hazards or interfere with the operating area.
                  </p>
                </div>
              </div>

              {/* Operational Advisory Strip */}
              <div className="bg-muted/40 border border-border p-4 flex items-start gap-3.5">
                <ShieldAlert className="size-5 text-primary shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <h3 className="font-mono text-xs uppercase tracking-wider font-semibold text-foreground">
                    Notice on Distance Guidelines
                  </h3>
                  <p className="font-sans text-xs md:text-sm text-muted-foreground leading-normal">
                    Refer directly to your equipment model&apos;s manual for layout boundaries and electrical enclosure anchoring requirements. Never guess placement tolerances.
                  </p>
                </div>
              </div>
            </CardContent>

            {/* Spec Plate Footer Action */}
            <div className="bg-muted/30 border-t border-border px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-4">
              <span className="font-mono text-xs text-muted-foreground">
                Mechanical Bull Shop // Commercial Safety Standards
              </span>
              <Button asChild variant="outline" size="sm" className="rounded-none border-border font-mono text-xs uppercase tracking-wider">
                <Link to="/SetupSafetyAndMaintenancePage">
                  <span>View Full Setup Specs</span>
                  <ArrowRight className="size-3.5 ml-1 text-primary" />
                </Link>
              </Button>
            </div>
          </Card>
        </motion.div>
      </div>
    </section>;
}